import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export interface ProcessStep {
  number: string;
  title: string;
  description: string;
  icon: string;
}

export interface ProcessSectionProps {
  headline: string;
  subheadline?: string;
  steps: ProcessStep[];
}

export default function ProcessSection({ headline, subheadline, steps }: ProcessSectionProps) {
  const head = useScrollAnimation();
  const body = useScrollAnimation();

  return (
    <section className="bg-jc-cream py-24 md:py-36 overflow-hidden relative">
      {/* Subtle dot pattern */}
      <div className="absolute inset-0 opacity-[0.025]"
        style={{ backgroundImage: 'radial-gradient(circle, #2A2A2A 1px, transparent 1px)', backgroundSize: '28px 28px' }}>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
        <div
          ref={head.ref}
          className="text-center mb-16 md:mb-20"
          style={{ opacity: head.visible ? 1 : 0, transform: head.visible ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-6 h-px bg-jc-gold"></span>
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-jc-gold">Our Process</span>
            <span className="w-6 h-px bg-jc-gold"></span>
          </div>
          <h2 className="font-serif text-[clamp(2rem,4vw,3.2rem)] font-light text-jc-charcoal leading-[1.12] max-w-xl mx-auto">
            {headline}
          </h2>
          {subheadline && (
            <p className="text-gray-400 text-[0.95rem] mt-5 max-w-lg mx-auto leading-relaxed">{subheadline}</p>
          )}
        </div>

        <div
          ref={body.ref}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0"
        >
          {steps.map((step, i) => (
            <div
              key={i}
              className="relative flex flex-col items-center text-center lg:px-8 group"
              style={{
                opacity: body.visible ? 1 : 0,
                transform: body.visible ? 'translateY(0)' : 'translateY(28px)',
                transition: `opacity 0.6s ease ${i * 0.12}s, transform 0.6s ease ${i * 0.12}s`,
              }}
            >
              {/* Connecting line */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-[38px] left-[calc(50%+44px)] right-[calc(-50%+44px)] h-px z-0">
                  <div className="w-full h-full border-t border-dashed border-jc-gold/25"></div>
                </div>
              )}

              {/* Step circle */}
              <div className="relative z-10 w-[76px] h-[76px] flex items-center justify-center bg-white border border-jc-sage/25 rounded-full mb-7 group-hover:border-jc-gold group-hover:shadow-[0_0_0_6px_rgba(201,169,97,0.06)] transition-all duration-400">
                <i className={`${step.icon} text-jc-sage text-2xl group-hover:text-jc-gold transition-colors duration-300`}></i>
                <div className="absolute -top-2.5 -right-2.5 w-6 h-6 bg-jc-gold rounded-full flex items-center justify-center shadow-sm">
                  <span className="text-jc-charcoal text-[10px] font-bold">{step.number}</span>
                </div>
              </div>

              <h3 className="font-serif text-[1.2rem] font-medium text-jc-charcoal mb-3 leading-tight">{step.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>

        <div
          className="mt-16 text-center"
          style={{ opacity: body.visible ? 1 : 0, transition: 'opacity 0.6s ease 0.6s' }}
        >
          <a href="#quote"
            className="inline-flex items-center gap-2.5 px-8 py-4 bg-jc-charcoal text-white font-bold text-[10px] tracking-[0.18em] uppercase rounded-full hover:bg-jc-gold hover:text-jc-charcoal transition-all duration-300 whitespace-nowrap group">
            Start Your Experience
            <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
